import { useSyncExternalStore } from 'react';

export interface WindowScroll {
  x: number;
  y: number;
}

// useSyncExternalStore is the React API for subscribing to an external
// mutable source like window scroll. It also makes the hook hydration-safe
// for free: the first client render uses the server snapshot (0/0, matching
// the SSR markup even when the browser restored a scroll position), then
// React immediately re-renders with the real position after hydration.
const SERVER_SNAPSHOT: WindowScroll = { x: 0, y: 0 };

// Snapshots must be referentially stable while the values are unchanged.
let snapshot: WindowScroll = SERVER_SNAPSHOT;

const getSnapshot = (): WindowScroll => {
  const { scrollX, scrollY } = window;
  if (snapshot.x !== scrollX || snapshot.y !== scrollY) {
    snapshot = { x: scrollX, y: scrollY };
  }
  return snapshot;
};

const getServerSnapshot = (): WindowScroll => SERVER_SNAPSHOT;

const subscribe = (onStoreChange: () => void): (() => void) => {
  window.addEventListener('scroll', onStoreChange, { passive: true });
  return () => window.removeEventListener('scroll', onStoreChange);
};

const useWindowScroll = (): WindowScroll =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

export default useWindowScroll;
